import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import * as inventoryService from "../../services/inventoryService";
import * as inventoryStock from "../../services/inventoryStockService";
import { getRole } from "../../components/Apicall";
import { formatDecimal } from "../../utils/numberFormat";
import PartsPanel from "./PartsPanel";

/**
 * "Inventory / Parts Used" section for the booking screen (Feature 4).
 * Deliberately a co-located sibling file to Bookings.jsx rather than an
 * inline addition to that 4599-line component - wiring into Bookings.jsx
 * itself is limited to one import + one render call + lifting the parts
 * subtotal for display, so this stays low-risk to the existing booking flow.
 *
 * Only usable once the booking has a real ezp_bookings row id (bookingId
 * prop) - i.e. an existing booking being edited, not a brand-new one still
 * being drafted (new bookings use StagedPartsSection.jsx instead, which
 * stages parts locally pre-submission). Cost fields and price override are
 * role=3 only; every other authenticated role can search + add parts at
 * default prices, per spec.
 *
 * `onPartsChanged(subtotal)` fires (via an effect on `subtotal`, not from
 * handleAdd/handleRemove directly) on initial load and after every
 * add/remove, so Bookings.jsx can fold this section's live subtotal into its
 * own price summary/Balance Due - parts DO now affect the booking's total,
 * unlike this section's original (Feature 4) design.
 */
// " · SN1, SN2" for a part line with serial numbers chosen.
const trackingText = (p) => {
  let list = p.tracking_details;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch (e) {
      list = null;
    }
  }
  return Array.isArray(list) && list.length ? ` · ${list.map((d) => d.serial_no || d.lot_no).join(", ")}` : "";
};

function PartsUsedSection({ bookingId, currency = "", onPartsChanged }) {
  const isAdmin = getRole() === "3";
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [overridePrice, setOverridePrice] = useState("");
  const [adding, setAdding] = useState(false);
  // Inventory upgrade: which warehouse the part comes from (when there is more than one), and the
  // serial numbers of a serial-tracked item (batches go earliest expiry first by themselves).
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [serials, setSerials] = useState("");
  useEffect(() => {
    // a failure (or no warehouses) leaves the section as it always was: the default warehouse
    Promise.resolve()
      .then(() => inventoryStock.listWarehouses({ status: "ACTIVE" }))
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setWarehouses(list);
        const def = list.find((w) => w.is_default);
        if (def) setWarehouseId(String(def.id));
      })
      .catch(() => setWarehouses([]));
  }, []);

  const loadParts = () => {
    if (!bookingId) return;
    setLoading(true);
    inventoryService
      .getBookingParts(bookingId)
      .then((res) => {
        if (res?.success === false) {
          toast.error(res.message || "Could not load parts used");
        }
        setParts(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => toast.error(err.message || "Could not load parts used"))
      .finally(() => setLoading(false));
  };

  useEffect(loadParts, [bookingId]);

  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      inventoryService
        .getItems({ search, is_active: 1, limit: 10 })
        .then((res) => setResults(res?.data || []));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const subtotal = parts.reduce((sum, p) => sum + Number(p.quantity) * Number(p.unit_price), 0);
  const taxTotal = parts.reduce((sum, p) => sum + Number(p.tax_amount || 0), 0);

  // Lets the booking form fold this into its own price/total display. Fires
  // on initial load (once parts have fetched) as well as after every
  // add/remove, since both cases change `subtotal` via `parts`.
  useEffect(() => {
    onPartsChanged?.(subtotal);
  }, [subtotal]);

  const handleAdd = async () => {
    if (!selectedItem || !quantity) return;
    setAdding(true);
    try {
      const payload = { item_id: selectedItem.id, quantity: Number(quantity) };
      if (warehouses.length > 1 && warehouseId) payload.warehouse_id = Number(warehouseId);
      if (selectedItem.tracking === "SERIAL") {
        const list = serials.split(/[\s,]+/).filter(Boolean);
        if (list.length !== Number(quantity)) {
          toast.error(`Enter ${quantity} serial number${Number(quantity) === 1 ? "" : "s"} for ${selectedItem.name}`);
          setAdding(false);
          return;
        }
        payload.tracking_details = list.map((serial_no) => ({ serial_no }));
      }
      if (isAdmin && overridePrice !== "") payload.unit_price = Number(overridePrice);
      const res = await inventoryService.addBookingPart(bookingId, payload);
      if (res?.success === false) {
        toast.error(res.message || "Could not add part");
      } else {
        setSelectedItem(null);
        setSearch("");
        setQuantity(1);
        setOverridePrice("");
        setSerials("");
        loadParts();
      }
    } catch (err) {
      toast.error(err.message || "Could not add part");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (partId) => {
    try {
      await inventoryService.deleteBookingPart(bookingId, partId);
      loadParts();
    } catch (err) {
      toast.error(err.message || "Could not remove part");
    }
  };

  if (!bookingId) {
    return (
      <div className="text-muted mt-3" style={{ fontSize: 13 }}>
        Save this booking first to add parts used from inventory.
      </div>
    );
  }

  return (
    <PartsPanel
      currency={currency}
      total={subtotal}
      loading={loading}
      emptyText="No parts added to this booking yet."
      rows={parts.map((p) => ({
        id: p.id,
        name: `${p.item_name}${warehouses.length > 1 && p.warehouse_code ? ` · ${p.warehouse_code}` : ""}${trackingText(p)}`,
        quantity: p.quantity,
        unit: p.unit_of_measure,
        unitPrice: p.unit_price,
        lineTotal: Number(p.quantity) * Number(p.unit_price),
        // a part whose stock was already deducted can no longer be removed
        onRemove: p.stock_deducted ? undefined : () => handleRemove(p.id)
      }))}
      footer={
        taxTotal > 0 && (
          <div className="d-flex justify-content-between px-4 py-2 border-top text-muted" style={{ fontSize: 13 }}>
            <span>Parts Tax</span>
            <span>
              {currency} {taxTotal.toFixed(2)}
            </span>
          </div>
        )
      }
    >
      <div>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Control
            style={{ maxWidth: 220 }}
            size="sm"
            placeholder="Search item by name/SKU…"
            value={selectedItem ? selectedItem.name : search}
            onChange={(e) => {
              setSelectedItem(null);
              setSearch(e.target.value);
            }}
          />
          <Form.Control
            type="number"
            size="sm"
            style={{ maxWidth: 90 }}
            min="0.001"
            step="0.001"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
          {isAdmin && selectedItem && (
            <div className="d-flex align-items-center" style={{ fontSize: 13, color: "#374151" }}>
              Unit price: <strong className="ms-1">{currency} {formatDecimal(selectedItem.selling_price)}</strong>
            </div>
          )}
          {isAdmin && (
            <Form.Control
              type="number"
              size="sm"
              style={{ maxWidth: 110 }}
              placeholder="Price override"
              step="0.01"
              value={overridePrice}
              onChange={(e) => setOverridePrice(e.target.value)}
            />
          )}
          {warehouses.length > 1 && (
            <Form.Select size="sm" style={{ maxWidth: 180 }} aria-label="Warehouse" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} – {w.name}
                </option>
              ))}
            </Form.Select>
          )}
          {selectedItem && selectedItem.tracking === "SERIAL" && (
            <Form.Control size="sm" style={{ maxWidth: 200 }} placeholder="Serial number(s)" aria-label="Serial numbers" value={serials} onChange={(e) => setSerials(e.target.value)} />
          )}
          <Button size="sm" disabled={!selectedItem || adding} onClick={handleAdd} style={{ background: "var(--button-color)", border: "none", color: "#fff" }}>
            {adding ? "Adding…" : "+ Add"}
          </Button>
        </div>
        {selectedItem && Number(quantity) > Number(selectedItem.current_qty) && (
          <div className="mt-1" style={{ fontSize: 12, color: "#D97706" }}>
            ⚠ Only {selectedItem.current_qty} {selectedItem.unit_of_measure} in stock — adding {quantity} will take stock negative.
          </div>
        )}
        {results.length > 0 && !selectedItem && (
          <div className="border rounded mt-1" style={{ maxWidth: 320, background: "#fff", position: "relative", zIndex: 5 }}>
            {results.map((item) => (
              <div
                key={item.id}
                className="px-2 py-1"
                style={{ cursor: "pointer", fontSize: 13 }}
                onClick={() => {
                  setSelectedItem(item);
                  setResults([]);
                }}
              >
                {item.name} ({item.sku}) — {item.current_qty} {item.unit_of_measure} in stock
              </div>
            ))}
          </div>
        )}
      </div>
    </PartsPanel>
  );
}

export default PartsUsedSection;
